import React, { useMemo } from "react";
import { BellRing, MessageCircle, ArrowRight, AlertTriangle } from "lucide-react";

/* Recordatorio de soporte para pedidos ACTIVOS.
 *
 * Reutiliza el estado de órdenes que ya mantiene la app (sin polling propio):
 * se muestra mientras exista un pedido cuyo estado no sea Completado ni
 * Rechazado, y desaparece solo cuando todos cierran. Al pulsarlo abre el chat
 * del pedido activo más reciente. */
const CLOSED_STATUSES = ["Completado", "Rechazado"];

export function ActiveOrderSupportReminder({ orders, onOpenChat }) {
  const activeOrder = useMemo(() => {
    const active = (orders || []).filter((o) => !CLOSED_STATUSES.includes(o.status));
    if (active.length === 0) return null;
    return active?.reduce((latest, o) =>
      new Date(o.createdAt || 0) > new Date(latest.createdAt || 0) ? o : latest,
    );
  }, [orders]);

  if (!activeOrder) return null;

  const activeCount = (orders || []).filter((o) => !CLOSED_STATUSES.includes(o.status)).length;
  const unread = (orders || []).reduce((a, o) => a + Number(o.unread_messages || 0), 0);

  return (
    <button
      type="button"
      onClick={() => onOpenChat(activeOrder)}
      data-testid="active-order-support-reminder"
      className="group relative w-full overflow-hidden rounded-2xl border-2 border-amber-400/85 bg-[#211907] p-1 text-left shadow-[0_0_28px_-9px_rgba(251,191,36,0.8)] transition-[transform,border-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-[0_0_36px_-7px_rgba(251,191,36,0.95)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-200 focus-visible:ring-offset-2 focus-visible:ring-offset-[#08090d]"
    >
      <span className="pointer-events-none absolute inset-y-0 left-0 w-1.5 bg-amber-300" aria-hidden="true" />
      <span className="relative flex items-center gap-3 bg-[#171207]/95 px-3 py-3 sm:gap-4 sm:px-4">
        <span className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border-2 border-amber-200 bg-amber-400 text-[#211907] shadow-[0_0_18px_rgba(251,191,36,0.65)] sm:h-14 sm:w-14">
          <BellRing className="h-6 w-6 animate-[pulse_1.4s_ease-in-out_infinite] sm:h-7 sm:w-7" />
          <span className="absolute inset-0 rounded-xl border-2 border-amber-200 animate-ping opacity-50" aria-hidden="true" />
          {unread > 0 && (
            <span
              data-testid="active-order-support-unread"
              className="absolute -top-2 -right-2 flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-[#171207] bg-red-500 px-1 text-[11px] font-black text-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span data-testid="active-order-support-status" className="mb-1 flex items-center gap-1.5 text-[10px] font-black tracking-[0.08em] text-amber-300 sm:text-xs">
            <AlertTriangle className="h-3.5 w-3.5" /> ACCIÓN REQUERIDA
          </span>
          <span data-testid="active-order-support-heading" className="block text-sm font-black text-white sm:text-base">
            Revisa el chat de soporte de tu pedido
          </span>
          <span data-testid="active-order-support-message" className="mt-1 block text-xs font-medium leading-snug text-amber-100 sm:text-sm">
            Tu pedido está en proceso. Nuestro equipo podría contactarte para completar tu solicitud.
          </span>
          <span data-testid="active-order-support-open-chat" className="mt-3 flex items-center gap-1.5 text-xs font-black text-amber-300 transition-transform duration-200 group-hover:translate-x-0.5">
            <MessageCircle className="h-4 w-4" /> ABRIR CHAT DEL PEDIDO{" "}
            {activeOrder.orderNumber != null
              ? `#${String(activeOrder.orderNumber).padStart(4, "0")}`
              : activeOrder.id}
            {activeCount > 1 ? ` · ${activeCount} pedidos activos` : ""}
            <ArrowRight className="h-4 w-4" />
          </span>
        </span>
        <span className="hidden h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border border-amber-300 bg-amber-400 text-[#211907] transition-transform duration-200 group-hover:translate-x-0.5 sm:flex" aria-hidden="true">
          <ArrowRight className="h-5 w-5" />
        </span>
      </span>
    </button>
  );
}
